<template>
  <BaseDialog title="核銷" width="700px" @close="$emit('close')" @confirm="onConfirm">
    <div v-loading="loading" class="dialog-body flex flex-col gap-[16px]">
      <div class="field">
        <span class="field-label">票匣名稱</span>
        <span>{{ record.name || '-' }}</span>
      </div>
      <div class="field">
        <div class="flex items-center justify-start gap-[8px]">
          <span class="field-label">票匣剩餘張數</span>
          <span class="text-sub text-gray-60">更新時間：{{ formatDateTime(record.updatedAt) }}</span>
        </div>
        <span>{{ record.availableUseTimes }} 張</span>
      </div>
      <div class="field">
        <span class="field-label">
          票匣核銷數量
          <span class="text-danger">*</span>
        </span>
        <BaseElInputNumber
          v-model="redeemTimes"
          :class="{ 'is-error': hasError }"
          class="redeem-input"
          :min="1"
          :max="maxTimes"
        />
        <p v-if="hasError" class="text-danger text-sub">
          超過剩餘提領核銷次數
        </p>
      </div>
      <p class="text-sub text-gray-60">核銷後將無法復原，確認核銷？</p>
    </div>
    <template #btnContainer>
      <div class="flex justify-end gap-[8px]">
        <BaseElButton plain @click="$emit('close')">取消</BaseElButton>
        <BaseElButton type="primary" :loading="submitting" :disabled="hasError || redeemTimes < 1" @click="onConfirm">確認</BaseElButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useShop } from '@/use/shop'
import { FindTicketBoxRecord, RedeemTicketBoxRecord } from '@/api/ticketBoxRecord'
import { formatDate } from '@/utils/date'
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BaseElInputNumber from '@/components/BaseElInputNumber.vue'

export default defineComponent({
  name: 'TicketBoxRedeemDialog',
  components: { BaseDialog, BaseElInputNumber },
  props: {
    recordId: { type: String, required: true },
  },
  emits: ['close', 'confirm'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const loading = ref(false)
    const submitting = ref(false)
    const record = ref({})
    const redeemTimes = ref(1)

    const maxTimes = computed(() => record.value.availableUseTimes || 0)
    const hasError = computed(() => redeemTimes.value > maxTimes.value || redeemTimes.value < 1)

    const formatDateTime = (time) => time ? formatDate(time, 'YYYY/MM/DD HH:mm') : '-'

    const fetchRecord = async () => {
      loading.value = true
      const [res, err] = await FindTicketBoxRecord({
        shopId: shopId.value,
        id: props.recordId,
      })
      loading.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      record.value = res
    }

    const onConfirm = async () => {
      if (hasError.value || submitting.value) return
      submitting.value = true
      const [, err] = await RedeemTicketBoxRecord({
        shopId: shopId.value,
        id: props.recordId,
        times: redeemTimes.value,
      })
      submitting.value = false
      if (err) {
        window.$message.error(err)
        return
      }
      window.$message.success('核銷成功')
      emit('confirm')
    }

    onMounted(() => {
      fetchRecord()
    })

    return {
      loading,
      submitting,
      record,
      redeemTimes,
      maxTimes,
      hasError,
      formatDateTime,
      onConfirm,
    }
  },
})
</script>

<style lang="postcss" scoped>
.dialog-body {
  min-width: 360px;
}
.field {
  @apply flex flex-col gap-[4px];
}
.field-label {
  @apply text-gray-100 font-medium text-sub;
}
::v-deep .redeem-input.el-input-number input {
  @apply max-w-[500px] w-full;
}
::v-deep .redeem-input.el-input-number {
  @apply max-w-[500px] w-full;
}
.is-error ::v-deep .el-input__inner {
  @apply border-danger;
}
</style>
